"use client";

import { useEffect, useRef, useState } from "react";

export default function LazyOnVisible({
  loader,
  rootMargin = "400px 0px",
  className,
  fallback = null,
  componentProps,
}) {
  const hostRef = useRef(null);
  const loadingRef = useRef(null);
  const [Component, setComponent] = useState(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || Component) return;

    const load = () => {
      if (loadingRef.current) return loadingRef.current;
      loadingRef.current = loader().then((mod) => {
        setComponent(() => mod.default);
        return mod.default;
      });
      return loadingRef.current;
    };

    if (!("IntersectionObserver" in window)) {
      load();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          load();
        }
      },
      { rootMargin }
    );

    observer.observe(host);
    return () => observer.disconnect();
  }, [Component, loader, rootMargin]);

  return (
    <div
      ref={hostRef}
      className={className}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: "140px",
        flex: "1 1 auto",
        alignSelf: "stretch",
      }}
    >
      {Component ? <Component {...componentProps} /> : fallback}
    </div>
  );
}
